import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { Box, CircularProgress, Typography } from '@mui/material';

import PageWithLayout from 'src/components/layout/PageWithLayout';
import AIModelClassLabels from 'src/features/ai_model/components/AIModelClassLabels';
import AIModelDetailForm from 'src/features/ai_model/components/AIModelDetailForm';
import { IAIModel } from 'src/features/ai_model/domain/types';
import { getAIModel, AI_MODEL_LOADING_KEY } from 'src/features/ai_model/store';
import AIModelFilesTable from 'src/features/ai_model_file/components/AIModelFilesTable';
import { AppDispatch, RootState } from 'src/store';

const AIModelDetailPage: React.FC = () => {
	const { siteSlug, aiModelId } = useParams<{
		siteSlug: string;
		aiModelId: string;
	}>();
	const dispatch = useDispatch<AppDispatch>();

	const siteDetails = useSelector(
		(state: RootState) => state.site.siteDetails,
	);
	const isLoading = useSelector(
		(state: RootState) => state.loading.loadingStates[AI_MODEL_LOADING_KEY],
	);

	const [aiModel, setAiModel] = useState<IAIModel | null>(null);
	const [aiModelFileCount, setAiModelFileCount] = useState<number>(0);

	const hasClassLabels = Boolean(
		aiModel?.class_labels && Object.keys(aiModel.class_labels).length > 0,
	);

	useEffect(() => {
		if (siteSlug && aiModelId) {
			dispatch(
				getAIModel({
					aiModelId: parseInt(aiModelId),
					siteSlug: siteSlug,
					onSuccess: (data) => {
						setAiModel(data);
					},
				}),
			);
		}
	}, [siteSlug, aiModelId, dispatch]);

	const breadcrumbs = [
		{
			label: siteDetails?.name || 'Site Details',
			href: `/site/${siteSlug}`,
		},
		{
			label: 'AI Models',
			href: `/site/${siteSlug}/ai-models`,
		},
		{ label: aiModel?.name || 'AI Model Details' },
	];

	return (
		<PageWithLayout breadcrumbs={breadcrumbs}>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					marginTop: '20px',
					gap: 2,
				}}
			>
				{/* Top Section: AI Model Detail Form */}
				<Box sx={{ flexShrink: 0 }}>
					{isLoading && !aiModel ? (
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'center',
								mt: 10,
							}}
						>
							<CircularProgress />
						</Box>
					) : aiModel ? (
						<AIModelDetailForm aiModel={aiModel} />
					) : (
						<Typography variant='h6' color='error'>
							Failed to load AI model details.
						</Typography>
					)}
				</Box>

				<AIModelClassLabels
					aiModel={aiModel}
					siteSlug={siteSlug}
					hasAIModelFiles={aiModelFileCount > 0}
					onAiModelUpdate={setAiModel}
				/>

				{/* Bottom Section: AI Model Files Table */}
				<Box>
					{aiModel && aiModelId && (
						<AIModelFilesTable
							aiModelId={parseInt(aiModelId)}
							canAddFiles={hasClassLabels}
							onFileCountChange={setAiModelFileCount}
						/>
					)}
				</Box>
			</Box>
		</PageWithLayout>
	);
};

export default AIModelDetailPage;
